import { useEffect, useState } from 'react';
import { ClientPageLayout } from '@/components/client/shared/ClientPageLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Circle, AlertCircle, Bot } from 'lucide-react';
import { clientApiService, BotResponse } from '@/services/api/client.service';
import { useAuth } from '@/hooks/useAuth';

export function ClientAssistant() {
  const { user } = useAuth();
  const [bot, setBot] = useState<BotResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Get client ID from user context
  const clientId = user?.client_project_id || '86a999a8-9952-428d-a273-83efcb6330fd';
  
  useEffect(() => {
    fetchBotData();
  }, []);
  
  const fetchBotData = async () => {
    try {
      setLoading(true);
      setError(null);
      const bots = await clientApiService.getClientBots(clientId);
      if (bots && bots.length > 0) {
        setBot(bots[0]); // Get the first bot
      } else {
        setError('No se encontró información del asistente');
      }
    } catch (err) {
      console.error('Error fetching bot data:', err);
      setError('Error al cargar la información del asistente');
    } finally {
      setLoading(false);
    }
  };
  
  
  if (loading) {
    return (
      <ClientPageLayout
        title="Mi Asistente"
        subtitle="Información y rendimiento de tu asistente virtual"
        showBackButton={true}
      >
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-6">
              <Skeleton className="w-20 h-20 rounded-full" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-6 w-48" />
                <Skeleton className="h-4 w-64" />
                <Skeleton className="h-5 w-32" />
              </div>
            </div>
          </CardContent>
        </Card>
      </ClientPageLayout>
    );
  }
  
  if (error && !bot) {
    return (
      <ClientPageLayout
        title="Mi Asistente"
        subtitle="Información y rendimiento de tu asistente virtual"
        showBackButton={true}
      >
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </ClientPageLayout>
    );
  }
  
  return (
    <ClientPageLayout
      title="Mi Asistente"
      subtitle="Información de tu asistente virtual"
      showBackButton={true}
    >
      {bot && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bot className="h-5 w-5" />
              Asistente Virtual
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {/* Nombre del asistente */}
              <div>
                <label className="text-sm font-medium text-muted-foreground">Nombre</label>
                <p className="text-xl font-semibold mt-1">{bot.name}</p>
              </div>
              
              {/* Estado del asistente */}
              <div>
                <label className="text-sm font-medium text-muted-foreground">Estado</label>
                <div className="flex items-center gap-2 mt-2">
                  <Badge 
                    variant="outline" 
                    className={
                      bot.status === 'active' 
                        ? "text-green-600 border-green-600 bg-green-50" 
                        : bot.status === 'maintenance'
                        ? "text-yellow-600 border-yellow-600 bg-yellow-50"
                        : "text-gray-600 border-gray-600 bg-gray-50"
                    }
                  >
                    <Circle className="w-2 h-2 fill-current mr-1" />
                    {bot.status === 'active' ? 'Activo' : 
                     bot.status === 'maintenance' ? 'En mantenimiento' : 'Inactivo'}
                  </Badge>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </ClientPageLayout>
  );
}